/**
 * TrendChart Component
 *
 * Line/Area chart for displaying score trends over time.
 * Supports multiple toggleable series (Overall, Compliance, Efficiency, Safety).
 */

import { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { TrendDataPoint } from '../../../hooks/useAnalytics';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';

interface TrendChartProps {
  data: TrendDataPoint[];
  isLoading?: boolean;
}

type SeriesKey = 'overall' | 'compliance' | 'efficiency' | 'safety';

interface SeriesConfig {
  key: SeriesKey;
  label: string;
  color: string;
  gradientId: string;
}

const SERIES_CONFIG: SeriesConfig[] = [
  { key: 'overall', label: 'Overall', color: '#00674a', gradientId: 'overallGradient' },
  { key: 'compliance', label: 'Compliance', color: '#3b82f6', gradientId: 'complianceGradient' },
  { key: 'efficiency', label: 'Efficiency', color: '#8b5cf6', gradientId: 'efficiencyGradient' },
  { key: 'safety', label: 'Safety', color: '#f59e0b', gradientId: 'safetyGradient' },
];

export function TrendChart({ data, isLoading }: TrendChartProps) {
  // Track which series are visible
  const [visibleSeries, setVisibleSeries] = useState<Set<SeriesKey>>(
    new Set(['overall', 'compliance', 'efficiency', 'safety'])
  );

  const toggleSeries = (key: SeriesKey) => {
    setVisibleSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Don't allow disabling all series
        if (next.size > 1) {
          next.delete(key);
        }
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Format date for X-axis
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ value: number | null; dataKey: string; color: string }>;
    label?: string;
  }) => {
    if (!active || !payload?.length || !label) return null;

    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="mb-2 text-xs font-medium text-gray-500">
          {new Date(label).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
        <div className="space-y-1">
          {payload.map((entry) => {
            const config = SERIES_CONFIG.find((s) => s.key === entry.dataKey);
            if (!config || entry.value === null) return null;
            return (
              <div key={entry.dataKey} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: config.color }}
                  />
                  <span className="text-sm text-gray-600">{config.label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {entry.value.toFixed(1)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-80 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-80 flex-col items-center justify-center text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
          <svg
            className="h-8 w-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
            />
          </svg>
        </div>
        <p className="mt-4 text-sm font-medium text-gray-900">No trend data available</p>
        <p className="mt-1 text-sm text-gray-500">
          Complete some assessments to see performance trends.
        </p>
      </div>
    );
  }

  // Format chart data
  const chartData = data.map((point) => ({
    ...point,
    formattedDate: formatDate(point.date),
  }));

  return (
    <div className="space-y-4">
      {/* Series Toggle Buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-500">Show:</span>
        {SERIES_CONFIG.map((series) => {
          const isActive = visibleSeries.has(series.key);
          return (
            <button
              key={series.key}
              onClick={() => toggleSeries(series.key)}
              className={`
                inline-flex items-center gap-1.5 rounded-full px-3 py-1.5
                text-xs font-medium transition-all duration-200
                ${
                  isActive
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }
              `}
            >
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: isActive ? series.color : '#9ca3af' }}
              />
              {series.label}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              {SERIES_CONFIG.map((series) => (
                <linearGradient
                  key={series.gradientId}
                  id={series.gradientId}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={series.color} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={series.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              interval="preserveStartEnd"
            />

            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              tickFormatter={(value) => `${value}`}
            />

            <Tooltip content={<CustomTooltip />} />

            {SERIES_CONFIG.map(
              (series) =>
                visibleSeries.has(series.key) && (
                  <Area
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    stroke={series.color}
                    strokeWidth={2}
                    fill={`url(#${series.gradientId})`}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                    connectNulls
                  />
                )
            )}

            <Legend
              content={() => null} // Hide default legend, using custom toggle buttons
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Assessment Count Footer */}
      {chartData.length > 0 && (
        <div className="flex items-center justify-center gap-4 border-t border-gray-100 pt-4">
          <span className="text-xs text-gray-400">
            Total assessments in period:{' '}
            <span className="font-medium text-gray-600">
              {chartData.reduce((sum, point) => sum + point.assessmentCount, 0)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

export default TrendChart;
